import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import { FiDownload, FiExternalLink, FiFileText, FiX } from "react-icons/fi";
import {
  formatNoticeFileSize,
  getNoticeAttachmentUrl,
  isNoticeImageAttachment,
  normalizeNoticeAttachments,
} from "../utils/lmsNotices";

async function downloadAttachment(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    // Fallback if fetch/CORS blocks force-download
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Renders notice attachments: image thumbnails (tap to enlarge) + doc download links.
 */
export function NoticeAttachments({ attachments, compact = false }) {
  const files = normalizeNoticeAttachments(attachments);
  const images = files.filter((f) => isNoticeImageAttachment(f));
  const docs = files.filter((f) => !isNoticeImageAttachment(f));
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const scrollYRef = useRef(0);
  const modalSize = useBreakpointValue({ base: "full", md: "xl" }) || "xl";

  const handleClose = useCallback(() => {
    onClose();
    setPreview(null);
    // Chakra scroll-lock + focus restore can jump the page; pin scroll back.
    const y = scrollYRef.current;
    requestAnimationFrame(() => {
      window.scrollTo(0, y);
      setTimeout(() => {
        window.scrollTo(0, y);
      }, 0);
    });
  }, [onClose]);

  if (files.length === 0) return null;

  const openPreview = (file) => {
    scrollYRef.current = window.scrollY || window.pageYOffset || 0;
    setPreview(file);
    onOpen();
  };

  const handleDownload = async () => {
    if (!preview) return;
    const url = getNoticeAttachmentUrl(preview.path);
    setDownloading(true);
    try {
      await downloadAttachment(url, preview.name);
    } finally {
      setDownloading(false);
    }
  };

  const thumbSize = compact ? { base: "72px", md: "64px" } : { base: "88px", md: "80px" };
  const previewUrl = preview ? getNoticeAttachmentUrl(preview.path) : "";

  return (
    <Box mt={2} mb={compact ? 1 : 2}>
      {images.length > 0 && (
        <HStack spacing={2} flexWrap="wrap" mb={docs.length > 0 ? 2 : 0}>
          {images.map((img) => {
            const url = getNoticeAttachmentUrl(img.path);
            return (
              <Box
                key={img.path}
                as="button"
                type="button"
                onClick={() => openPreview(img)}
                w={thumbSize}
                h={thumbSize}
                minW="44px"
                minH="44px"
                borderRadius="md"
                overflow="hidden"
                border="1px solid"
                borderColor="blackAlpha.200"
                bg="white"
                flexShrink={0}
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
                _active={{ opacity: 0.8 }}
                aria-label={`View ${img.name}`}
              >
                <Image
                  src={url}
                  alt={img.name}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  draggable={false}
                />
              </Box>
            );
          })}
        </HStack>
      )}

      {docs.length > 0 && (
        <VStack align="stretch" spacing={1}>
          {docs.map((doc) => {
            const url = getNoticeAttachmentUrl(doc.path);
            const sizeLabel = formatNoticeFileSize(doc.size);
            return (
              <HStack key={doc.path} spacing={2} align="center" minH="36px">
                <Link
                  href={url}
                  isExternal
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                  minW={0}
                  flex="1"
                  fontSize={compact ? "2xs" : "xs"}
                  color="blue.600"
                  fontWeight="500"
                  _hover={{ textDecoration: "underline" }}
                >
                  <Box as={FiFileText} flexShrink={0} />
                  <Text as="span" noOfLines={1}>
                    {doc.name}
                  </Text>
                  {sizeLabel ? (
                    <Text as="span" color="gray.400" flexShrink={0}>
                      ({sizeLabel})
                    </Text>
                  ) : null}
                </Link>
                <IconButton
                  aria-label={`Download ${doc.name}`}
                  icon={<FiDownload />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  flexShrink={0}
                  onClick={() => downloadAttachment(url, doc.name)}
                />
              </HStack>
            );
          })}
        </VStack>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size={modalSize}
        isCentered
        returnFocusOnClose={false}
        blockScrollOnMount
        preserveScrollBarGap
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(2px)" />
        <ModalContent
          m={{ base: 0, md: 4 }}
          borderRadius={{ base: 0, md: "xl" }}
          maxH={{ base: "100dvh", md: "90vh" }}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader
            py={3}
            px={4}
            pr={12}
            fontSize="sm"
            fontWeight="600"
            borderBottomWidth="1px"
            borderColor="gray.100"
            noOfLines={1}
          >
            {preview?.name || "Preview"}
          </ModalHeader>
          <ModalCloseButton top={2.5} />
          <ModalBody
            py={{ base: 4, md: 5 }}
            px={{ base: 3, md: 5 }}
            flex="1"
            overflowY="auto"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {preview && (
              <Image
                src={previewUrl}
                alt={preview.name}
                maxW="100%"
                maxH={{ base: "calc(100dvh - 160px)", md: "70vh" }}
                objectFit="contain"
                borderRadius="md"
              />
            )}
          </ModalBody>
          <ModalFooter
            gap={2}
            borderTopWidth="1px"
            borderColor="gray.100"
            py={3}
            px={4}
            flexWrap="wrap"
            justifyContent={{ base: "stretch", md: "flex-end" }}
          >
            <Button
              variant="ghost"
              leftIcon={<FiX />}
              onClick={handleClose}
              flex={{ base: 1, md: "initial" }}
            >
              Close
            </Button>
            <Button
              as="a"
              href={previewUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              leftIcon={<FiExternalLink />}
              flex={{ base: 1, md: "initial" }}
              isDisabled={!previewUrl}
            >
              Open
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<FiDownload />}
              onClick={handleDownload}
              isLoading={downloading}
              loadingText="Saving…"
              flex={{ base: "1 1 100%", md: "initial" }}
              order={{ base: -1, md: 0 }}
            >
              Download
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
